import { useCallback, useMemo, useRef, useState } from 'react';
import { createId } from '@paralleldrive/cuid2';
import type { BlockConfig, BlockItem, BlockSectionItem, BlockWidgetItem, DashboardConfig, SectionConfig, WidgetConfig, WidgetDataSource, WidgetSpecificConfig } from '@trinity/chat/types/dashboard-config';
import type { CanonicalWidgetData } from '@trinity/chat/types/dashboard-data';
import type { WidgetRegistry } from '@trinity/chat-components/components/PageDashboard/registry/WidgetRegistry';
import { getSectionEffectiveRows } from '../../components/PageDashboard/Builder/dashboardLayoutUtils';

function findWidgetEntry(
  config: DashboardConfig,
  widgetId: string,
): { blockId: string; row: number; slot: number; widgetType: string } | null {
  for (const block of config.blocks) {
    const result = findWidgetInBlockItems(block.id, block.items, widgetId);
    if (result !== undefined) return result;
  }
  return null;
}

function findWidgetInBlockItems(
  blockId: string,
  items: BlockItem[],
  widgetId: string,
): { blockId: string; row: number; slot: number; widgetType: string } | null | undefined {
  for (const item of items) {
    if (item.type === 'widget' && item.widget.id === widgetId) {
      return { blockId, row: item.row ?? 1, slot: item.slot, widgetType: item.widget.type };
    }
    if (item.type !== 'section') continue;
    for (const bw of item.section.widgets) {
      if (bw.widget.id === widgetId) return null;
    }
  }
  return undefined;
}

/**
 * Build a WidgetConfig from dynamic parts. The discriminated union can't be
 * inferred from a runtime `type` string + spread config, so this helper
 * centralises the single unavoidable cast.
 */
function buildWidgetConfig(
  id: string,
  type: string,
  config: Record<string, unknown>,
  dataSource?: WidgetDataSource,
): WidgetConfig {
  // @ts-expect-error — TS cannot narrow the discriminated union from a runtime `type` string.
  // The config shape is guaranteed by the registry's defaultConfig + caller-provided overrides.
  return { id, type, config, ...(dataSource ? { dataSource } : {}) };
}

/**
 * Spread-update an existing WidgetConfig. The input widget is already a valid
 * discriminated union member; spreading it with partial overrides preserves the
 * discriminant at runtime but TS can't verify it statically.
 */
function updateWidget<W>(widget: W, overrides: Partial<WidgetConfig>): WidgetConfig {
  // @ts-expect-error — spread of a discriminated union loses narrowing; safe because
  // the `type` field is preserved from the original widget.
  return { ...widget, ...overrides };
}
import {
  DashboardBuilderContext,
  DashboardBuilderEditingContext,
  DashboardConfigContext,
  DashboardMutationContext,
  WidgetRegistryContext,
} from './context';

// ─── Props ────────────────────────────────────────────────────────────────────

type Props = {
  children: React.ReactNode;
  /** Initial config loaded from the API (should already be schema-upgraded) */
  initialConfig?: DashboardConfig;
  /** Widget registry to expose to children */
  registry: WidgetRegistry;
  /** Called when the user saves the dashboard */
  onSave?: (config: DashboardConfig, description?: string) => Promise<void>;
  /** Called when the user cancels unsaved changes */
  onCancel?: () => void;
  /** Called when the user reverts to a previous version */
  onRevert?: (versionId: string) => Promise<void>;
  /**
   * When false, the "Data Source" tab is hidden from the widget config sidebar.
   * Set to false in apps that use their own native data sources instead of
   * HTTP agents (e.g. atrium-backend / sentiment frontend).
   * Default: true.
   */
  showDataSource?: boolean;
};

const DEFAULT_CONFIG: DashboardConfig = {
  blocks: [],
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * DashboardBuilderProvider — composes all dashboard contexts into a single
 * provider tree. Mount this at the top of every dashboard builder/renderer.
 *
 * Layer order (outer → inner):
 *   WidgetRegistryContext
 *   → DashboardConfigContext
 *   → DashboardBuilderContext (UI state)
 *   → DashboardBuilderEditingContext (mutations)
 *   → DashboardMutationContext (persistence)
 */
export function DashboardBuilderProvider({
  children,
  initialConfig = DEFAULT_CONFIG,
  registry,
  onSave,
  onCancel,
  onRevert,
  showDataSource = true,
}: Props): React.ReactElement {
  // ── Config ─────────────────────────────────────────────────────────────────
  const [config, setConfigState] = useState<DashboardConfig>(initialConfig);
  const [isDirty, setIsDirty] = useState(false);

  const setConfig = useCallback((updater: (prev: DashboardConfig) => DashboardConfig) => {
    setConfigState(prev => updater(prev));
    setIsDirty(true);
  }, []);

  const configValue = useMemo(() => ({ config, setConfig }), [config, setConfig]);

  // ── Widget Registry ────────────────────────────────────────────────────────
  const registryValue = useMemo(() => ({
    registry,
    getAvailableWidgets: () => registry.getAll(),
  }), [registry]);

  // ── Builder UI State ───────────────────────────────────────────────────────
  const [selectedBlockId, setSelectedBlockId] = useState<string | null>(null);
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
  const [sidebarPanel, setSidebarPanel] = useState<'none' | 'chart-config' | 'create-widget'>('none');
  const [pendingSlot, setPendingSlot] = useState<{ blockId: string; row: number; slot: number } | null>(null);
  const [pendingWidgetType, setPendingWidgetType] = useState<string | null>(null);

  const builderValue = useMemo(() => ({
    selectedBlockId,
    setSelectedBlockId,
    selectedWidgetId,
    setSelectedWidgetId,
    isDirty,
    setIsDirty,
    sidebarPanel,
    setSidebarPanel,
    pendingSlot,
    setPendingSlot,
    pendingWidgetType,
    setPendingWidgetType,
    showDataSource,
  }), [selectedBlockId, selectedWidgetId, isDirty, sidebarPanel, pendingSlot, pendingWidgetType, showDataSource]);

  // ── Editing Actions ────────────────────────────────────────────────────────

  const addBlock = useCallback((opts?: { title?: string }): string => {
    const blockId = createId();
    setConfig(prev => {
      const title = opts?.title ?? `Block ${prev.blocks.length + 1}`;
      const w = Math.min(3, prev.outerCols ?? 3);
      const newBlock: BlockConfig = { id: blockId, title, w, items: [] };
      return { ...prev, blocks: [...prev.blocks, newBlock] };
    });
    return blockId;
  }, [setConfig]);

  const addBlockWithWidget = useCallback((type: string, widgetConfig: Partial<WidgetSpecificConfig>, dataSource?: WidgetDataSource, rowSpanOverride?: number): string => {
    const blockId = createId();
    const widgetId = createId();
    const entry = registry.get(type);
    setConfig(prev => {
      // Count all existing widgets (direct + inside sections) to derive a default title
      const widgetCount = prev.blocks.reduce((n, b) => {
        for (const item of b.items) {
          if (item.type === 'widget') n++;
          else if (item.type === 'section') n += item.section.widgets.length;
          else {
            //nop
          }
        }
        return n;
      }, 0);
      const defaultTitle = `Widget ${widgetCount + 1}`;
      // User-provided widgetConfig title takes precedence; otherwise use the counter-based default
      const titleOverride = widgetConfig.title;
      const resolvedTitle = titleOverride ?? defaultTitle;
      const newWidget = buildWidgetConfig(
        widgetId,
        type,
        { ...entry?.defaultConfig, ...widgetConfig, title: resolvedTitle },
        dataSource,
      );
      // Block title mirrors the widget title so the 1×1 block reads as "Widget 1" in the layout
      const minW = entry?.minW ?? 1;
      const minH = entry?.minH ?? 1;
      const initSpan = Math.min(minW, prev.outerCols ?? 3);
      const initRowSpan = rowSpanOverride ?? (minH > 1 ? minH : undefined);
      const newWidgetItem: BlockWidgetItem = { type: 'widget', row: 1, slot: 1, span: initSpan, widget: newWidget, ...(initRowSpan ? { rowSpan: initRowSpan } : {}) };
      const newBlock: BlockConfig = { id: blockId, title: resolvedTitle, w: initSpan, items: [newWidgetItem], hideTitle: true };
      return { ...prev, blocks: [...prev.blocks, newBlock] };
    });
    return widgetId;
  }, [registry, setConfig]);

  const removeBlock = useCallback((blockId: string) => {
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.filter(b => b.id !== blockId),
    }));
  }, [setConfig]);

  const updateBlockTitle = useCallback((blockId: string, title: string) => {
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => b.id === blockId ? { ...b, title } : b),
    }));
  }, [setConfig]);

  const toggleBlockHideTitle = useCallback((blockId: string) => {
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => b.id === blockId ? { ...b, hideTitle: !(b.hideTitle ?? false) } : b),
    }));
  }, [setConfig]);

  const toggleBlockCollapsible = useCallback((blockId: string) => {
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => b.id === blockId ? { ...b, collapsible: !(b.collapsible ?? false) } : b),
    }));
  }, [setConfig]);

  const assignWidgetToSlot = useCallback((blockId: string, row: number, slot: number, type: string, widgetConfig: Partial<WidgetSpecificConfig>, dataSource?: WidgetDataSource, rowSpanOverride?: number): string => {
    const widgetId = createId();
    const entry = registry.get(type);
    const newWidget = buildWidgetConfig(
      widgetId,
      type,
      { ...entry?.defaultConfig, ...widgetConfig },
      dataSource,
    );
    const minW = entry?.minW ?? 1;
    const minH = entry?.minH ?? 1;
    const initRowSpan = rowSpanOverride ?? (minH > 1 ? minH : undefined);
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => {
        if (b.id !== blockId) return b;
        const initSpan = Math.min(minW, b.w ?? 3);
        const newItem: BlockWidgetItem = { type: 'widget', row, slot, span: initSpan, widget: newWidget, ...(initRowSpan ? { rowSpan: initRowSpan } : {}) };
        // Remove any existing item in the same row whose coverage includes this slot
        const filtered = b.items.filter(item => {
          if ((item.row ?? 1) !== row) return true;
          for (let s = item.slot; s < item.slot + item.span; s++) {
            if (s === slot) return false;
          }
          return true;
        });
        return { ...b, items: [...filtered, newItem] };
      }),
    }));
    return widgetId;
  }, [registry, setConfig]);

  const removeWidgetAtSlot = useCallback((blockId: string, row: number, slot: number) => {
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => {
        if (b.id !== blockId) return b;
        return { ...b, items: b.items.filter(item => !(item.type === 'widget' && item.slot === slot && (item.row ?? 1) === row)) };
      }),
    }));
  }, [setConfig]);

  const moveWidget = useCallback((fromBlockId: string, fromRow: number, fromSlot: number, toBlockId: string, toRow: number, toSlot: number) => {
    if (fromBlockId === toBlockId && fromRow === toRow && fromSlot === toSlot) return;
    setConfig(prev => {
      const fromBlock = prev.blocks.find(b => b.id === fromBlockId);
      if (!fromBlock) return prev;
      const movingItem = fromBlock.items.find(
        item => item.type === 'widget' && item.slot === fromSlot && (item.row ?? 1) === fromRow,
      ) as BlockWidgetItem | undefined;
      if (!movingItem) return prev;
      const toBlock = prev.blocks.find(b => b.id === toBlockId);
      const toBlockCols = toBlock?.w ?? 3;
      const effectiveSpan = Math.min(movingItem.span, (toBlockCols + 1) - toSlot);

      // Push any item in the destination row whose columns overlap the target range to
      // the next available row rather than deleting them.
      const pushDisplaced = (items: BlockItem[]): BlockItem[] => {
        const maxRow = items.reduce((m, w) => Math.max(m, w.row ?? 1), 0);
        const nextRow = Math.max(maxRow, toRow) + 1;
        return items.map(item => {
          if ((item.row ?? 1) !== toRow) return item;
          for (let s = toSlot; s < toSlot + effectiveSpan; s++) {
            if (s >= item.slot && s < item.slot + item.span) return { ...item, row: nextRow };
          }
          return item;
        });
      };

      return {
        ...prev,
        blocks: prev.blocks.map(b => {
          if (b.id === fromBlockId && b.id === toBlockId) {
            let its = b.items.filter(item => !(item.type === 'widget' && item.slot === fromSlot && (item.row ?? 1) === fromRow));
            its = pushDisplaced(its);
            return { ...b, items: [...its, { ...movingItem, row: toRow, slot: toSlot, span: effectiveSpan }] };
          }
          if (b.id === fromBlockId) {
            return { ...b, items: b.items.filter(item => !(item.type === 'widget' && item.slot === fromSlot && (item.row ?? 1) === fromRow)) };
          }
          if (b.id === toBlockId) {
            const its = pushDisplaced(b.items);
            return { ...b, items: [...its, { ...movingItem, row: toRow, slot: toSlot, span: effectiveSpan }] };
          }
          return b;
        }),
      };
    });
  }, [setConfig]);

  const updateWidgetSpan = useCallback((blockId: string, row: number, slot: number, span: number) => {
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => {
        if (b.id !== blockId) return b;
        const blockCols = b.w ?? 3;
        const effectiveSpan = Math.min(span, (blockCols + 1) - slot);
        const maxRow = b.items.reduce((m, item) => Math.max(m, item.row ?? 1), 1);
        const nextRow = maxRow + 1;
        // Update span for the target item; push other same-row items that are
        // now covered by the expanded span into the next available row.
        return {
          ...b,
          items: b.items.map(item => {
            if (item.type === 'widget' && item.slot === slot && (item.row ?? 1) === row) return { ...item, span: effectiveSpan };
            if ((item.row ?? 1) !== row) return item;
            // Check if this other same-row item overlaps the new coverage
            for (let s = slot; s < slot + effectiveSpan; s++) {
              if (s >= item.slot && s < item.slot + item.span) return { ...item, row: nextRow };
            }
            return item;
          }),
        };
      }),
    }));
  }, [setConfig]);

  const updateWidgetRowSpan = useCallback((blockId: string, row: number, slot: number, rowSpan: number) => {
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => {
        if (b.id !== blockId) return b;
        const clampedRowSpan = Math.max(1, rowSpan);
        const targetItem = b.items.find(
          i => i.type === 'widget' && i.slot === slot && (i.row ?? 1) === row,
        ) as BlockWidgetItem | undefined;
        if (!targetItem) return b;
        const prevRowSpan = targetItem.rowSpan ?? 1;

        // First, update the widget's rowSpan
        const updatedItems = b.items.map(item => {
          if (item.type === 'widget' && item.slot === slot && (item.row ?? 1) === row) {
            return { ...item, rowSpan: clampedRowSpan };
          }
          return item;
        });

        // If the rowSpan expanded, push items in the newly covered sub-rows that overlap
        // the widget's column footprint down to the first row after the expanded widget.
        if (clampedRowSpan <= prevRowSpan) {
          return { ...b, items: updatedItems };
        }
        const widgetSlotStart = slot;
        const widgetSlotEnd = widgetSlotStart + targetItem.span - 1;
        const landingRow = row + clampedRowSpan; // First row safely below the expanded widget

        return {
          ...b,
          items: updatedItems.map(item => {
            // Don't move the widget itself
            if (item.type === 'widget' && item.slot === slot && (item.row ?? 1) === row) return item;
            const itemRow = item.row ?? 1;
            // Only affect rows that are now absorbed by the expanded rowSpan (prevRowSpan..clampedRowSpan-1)
            if (itemRow < row + prevRowSpan || itemRow >= row + clampedRowSpan) return item;
            // Push only items whose columns overlap the expanding widget's column range
            const itemSlotEnd = item.slot + item.span - 1;
            const overlaps = item.slot <= widgetSlotEnd && itemSlotEnd >= widgetSlotStart;
            return overlaps ? { ...item, row: landingRow } : item;
          }),
        };
      }),
    }));
  }, [setConfig]);

  const configRef = useRef(config);
  configRef.current = config;

  const autoSizeWidget = useCallback((widgetId: string, data: CanonicalWidgetData) => {
    const entry = findWidgetEntry(configRef.current, widgetId);
    if (!entry) return;
    const regEntry = registry.get(entry.widgetType);
    const autoSpan = regEntry?.autoRowSpan?.(data);
    if (autoSpan != null && autoSpan >= 1) {
      updateWidgetRowSpan(entry.blockId, entry.row, entry.slot, autoSpan);
    }
  }, [registry, updateWidgetRowSpan]);

  const moveSection = useCallback((blockId: string, fromRow: number, fromSlot: number, toRow: number, toSlot: number) => {
    if (fromRow === toRow && fromSlot === toSlot) return;
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => {
        if (b.id !== blockId) return b;
        const movingItem = b.items.find(
          item => item.type === 'section' && item.slot === fromSlot && (item.row ?? 1) === fromRow,
        ) as BlockSectionItem | undefined;
        if (!movingItem) return b;
        const blockCols = b.w ?? 3;
        const effectiveSpan = Math.min(movingItem.span, (blockCols + 1) - toSlot);
        const maxRow = b.items.reduce((m, item) => Math.max(m, item.row ?? 1), 0);
        const nextRow = Math.max(maxRow, toRow) + 1;
        let its = b.items.filter(item => !(item.type === 'section' && item.slot === fromSlot && (item.row ?? 1) === fromRow));
        its = its.map(item => {
          if ((item.row ?? 1) !== toRow) return item;
          for (let s = toSlot; s < toSlot + effectiveSpan; s++) {
            if (s >= item.slot && s < item.slot + item.span) return { ...item, row: nextRow };
          }
          return item;
        });
        return { ...b, items: [...its, { ...movingItem, row: toRow, slot: toSlot, span: effectiveSpan }] };
      }),
    }));
  }, [setConfig]);

  /**
   * Move a widget out of a section and drop it into a block slot.
   * Displaced items at the destination slot are pushed to the next available row.
   */
  const moveWidgetFromSectionToBlock = useCallback((
    fromBlockId: string, fromSectionId: string, fromRow: number, fromSlot: number,
    toBlockId: string, toRow: number, toSlot: number,
  ) => {
    setConfig(prev => {
      const fromBlock = prev.blocks.find(b => b.id === fromBlockId);
      const sectionItem = fromBlock?.items.find(
        i => i.type === 'section' && i.section.id === fromSectionId,
      ) as BlockSectionItem | undefined;
      if (!sectionItem) return prev;
      const movingBw = sectionItem.section.widgets.find(
        bw => bw.slot === fromSlot && (bw.row ?? 1) === fromRow,
      );
      if (!movingBw) return prev;
      const toBlock = prev.blocks.find(b => b.id === toBlockId);
      const toBlockCols = toBlock?.w ?? 3;
      const effectiveSpan = Math.min(movingBw.span, (toBlockCols + 1) - toSlot);

      return {
        ...prev,
        blocks: prev.blocks.map(b => {
          let updated = b;
          // Remove from source section
          if (updated.id === fromBlockId) {
            updated = {
              ...updated,
              items: updated.items.map(item => {
                if (item.type !== 'section' || item.section.id !== fromSectionId) return item;
                return {
                  ...item,
                  section: {
                    ...item.section,
                    widgets: item.section.widgets.filter(
                      bw => !(bw.slot === fromSlot && (bw.row ?? 1) === fromRow),
                    ),
                  },
                };
              }),
            };
          }
          // Add to destination block slot, pushing displaced items
          if (updated.id === toBlockId) {
            const newItem: BlockWidgetItem = { type: 'widget', row: toRow, slot: toSlot, span: effectiveSpan, widget: movingBw.widget };
            const maxRow = updated.items.reduce((m, i) => Math.max(m, i.row ?? 1), 0);
            const nextRow = Math.max(maxRow, toRow) + 1;
            const pushed = updated.items.map(item => {
              if ((item.row ?? 1) !== toRow) return item;
              for (let s = toSlot; s < toSlot + effectiveSpan; s++) {
                if (s >= item.slot && s < item.slot + item.span) return { ...item, row: nextRow };
              }
              return item;
            });
            updated = { ...updated, items: [...pushed, newItem] };
          }
          return updated;
        }),
      };
    });
  }, [setConfig]);

  /**
   * Move a widget out of a block slot and drop it into a section slot.
   * Displaced section widgets at the destination slot are pushed to the next row.
   */
  const moveWidgetFromBlockToSection = useCallback((
    fromBlockId: string, fromRow: number, fromSlot: number,
    toBlockId: string, toSectionId: string, toRow: number, toSlot: number,
  ) => {
    setConfig(prev => {
      const fromBlock = prev.blocks.find(b => b.id === fromBlockId);
      const movingItem = fromBlock?.items.find(
        i => i.type === 'widget' && i.slot === fromSlot && (i.row ?? 1) === fromRow,
      ) as BlockWidgetItem | undefined;
      if (!movingItem) return prev;

      return {
        ...prev,
        blocks: prev.blocks.map(b => {
          let updated = b;
          // Remove from source block
          if (updated.id === fromBlockId) {
            updated = {
              ...updated,
              items: updated.items.filter(
                i => !(i.type === 'widget' && i.slot === fromSlot && (i.row ?? 1) === fromRow),
              ),
            };
          }
          // Add into target section
          if (updated.id === toBlockId) {
            updated = {
              ...updated,
              items: updated.items.map(item => {
                if (item.type !== 'section' || item.section.id !== toSectionId) return item;
                const sectionCols = item.span;
                const effectiveSpan = Math.min(movingItem.span, (sectionCols + 1) - toSlot);
                const maxSectionRow = item.section.widgets.reduce((m, w) => Math.max(m, w.row ?? 1), 0);
                const nextRow = Math.max(maxSectionRow, toRow) + 1;
                const pushed = item.section.widgets.map(bw => {
                  if ((bw.row ?? 1) !== toRow) return bw;
                  for (let s = toSlot; s < toSlot + effectiveSpan; s++) {
                    if (s >= bw.slot && s < bw.slot + bw.span) return { ...bw, row: nextRow };
                  }
                  return bw;
                });
                const newBw = { row: toRow, slot: toSlot, span: effectiveSpan, widget: movingItem.widget };
                return { ...item, section: { ...item.section, widgets: [...pushed, newBw] } };
              }),
            };
          }
          return updated;
        }),
      };
    });
  }, [setConfig]);

  /**
   * Move a widget from one section to another (same block or different block).
   * Displaced destination-section widgets are pushed to the next row.
   */
  const moveWidgetBetweenSections = useCallback((
    fromBlockId: string, fromSectionId: string, fromRow: number, fromSlot: number,
    toBlockId: string, toSectionId: string, toRow: number, toSlot: number,
  ) => {
    setConfig(prev => {
      const fromBlock = prev.blocks.find(b => b.id === fromBlockId);
      const sourceSectionItem = fromBlock?.items.find(
        i => i.type === 'section' && i.section.id === fromSectionId,
      ) as BlockSectionItem | undefined;
      if (!sourceSectionItem) return prev;
      const movingBw = sourceSectionItem.section.widgets.find(
        bw => bw.slot === fromSlot && (bw.row ?? 1) === fromRow,
      );
      if (!movingBw) return prev;

      return {
        ...prev,
        blocks: prev.blocks.map(b => {
          let updated = b;
          // Remove from source section
          if (updated.id === fromBlockId) {
            updated = {
              ...updated,
              items: updated.items.map(item => {
                if (item.type !== 'section' || item.section.id !== fromSectionId) return item;
                return {
                  ...item,
                  section: {
                    ...item.section,
                    widgets: item.section.widgets.filter(
                      bw => !(bw.slot === fromSlot && (bw.row ?? 1) === fromRow),
                    ),
                  },
                };
              }),
            };
          }
          // Add to destination section
          if (updated.id === toBlockId) {
            updated = {
              ...updated,
              items: updated.items.map(item => {
                if (item.type !== 'section' || item.section.id !== toSectionId) return item;
                const sectionCols = item.span;
                const effectiveSpan = Math.min(movingBw.span, (sectionCols + 1) - toSlot);
                const maxSectionRow = item.section.widgets.reduce((m, w) => Math.max(m, w.row ?? 1), 0);
                const nextRow = Math.max(maxSectionRow, toRow) + 1;
                const pushed = item.section.widgets.map(bw => {
                  if ((bw.row ?? 1) !== toRow) return bw;
                  for (let s = toSlot; s < toSlot + effectiveSpan; s++) {
                    if (s >= bw.slot && s < bw.slot + bw.span) return { ...bw, row: nextRow };
                  }
                  return bw;
                });
                const newBw = { row: toRow, slot: toSlot, span: effectiveSpan, widget: movingBw.widget };
                return { ...item, section: { ...item.section, widgets: [...pushed, newBw] } };
              }),
            };
          }
          return updated;
        }),
      };
    });
  }, [setConfig]);

  const updateWidgetConfig = useCallback(
    (widgetId: string, partial: Partial<WidgetConfig['config']>) => {
      setConfig(prev => ({
        ...prev,
        blocks: prev.blocks.map(b => ({
          ...b,
          items: b.items.map(item => {
            if (item.type === 'widget' && item.widget.id === widgetId) {
              return { ...item, widget: { ...item.widget, config: { ...item.widget.config, ...partial } } } as BlockWidgetItem;
            }
            if (item.type === 'section') {
              const updatedWidgets = item.section.widgets.map(bw =>
                bw.widget.id === widgetId
                  ? { ...bw, widget: updateWidget(bw.widget, { config: { ...bw.widget.config, ...partial } }) }
                  : bw,
              );
              return { ...item, section: { ...item.section, widgets: updatedWidgets } };
            }
            return item;
          }),
        })),
      }));
    },
    [setConfig],
  );

  const updateWidgetDataSource = useCallback(
    (widgetId: string, dataSource: WidgetDataSource) => {
      setConfig(prev => ({
        ...prev,
        blocks: prev.blocks.map(b => ({
          ...b,
          items: b.items.map(item => {
            if (item.type === 'widget' && item.widget.id === widgetId) {
              return { ...item, widget: { ...item.widget, dataSource } };
            }
            if (item.type === 'section') {
              const updatedWidgets = item.section.widgets.map(bw =>
                bw.widget.id === widgetId
                  ? { ...bw, widget: updateWidget(bw.widget, { dataSource }) }
                  : bw,
              );
              return { ...item, section: { ...item.section, widgets: updatedWidgets } };
            }
            return item;
          }),
        })),
      }));
    },
    [setConfig],
  );

  const reorderBlocks = useCallback((orderedIds: string[]) => {
    setConfig(prev => {
      const blockMap = new Map(prev.blocks.map(b => [b.id, b]));
      const reordered = orderedIds
        .map(id => blockMap.get(id))
        .filter((b): b is BlockConfig => b !== undefined);
      // Append any blocks not in the orderedIds list (safety net)
      const reorderedSet = new Set(orderedIds);
      const remaining = prev.blocks.filter(b => !reorderedSet.has(b.id));
      return { ...prev, blocks: [...reordered, ...remaining] };
    });
  }, [setConfig]);

  /**
   * Called when a drag ends — re-orders blocks by visual (y, x) position AND
   * persists each block's column (x) in a single config update.
   */
  const updateBlocksFromLayout = useCallback((orderedIds: string[], xMap: Record<string, number>) => {
    setConfig(prev => {
      const blockMap = new Map(prev.blocks.map(b => [b.id, b]));
      const reorderedSet = new Set(orderedIds);
      const reordered = orderedIds
        .map(id => blockMap.get(id))
        .filter((b): b is BlockConfig => b !== undefined)
        .map(b => xMap[b.id] !== undefined ? { ...b, x: xMap[b.id] } : b);
      const remaining = prev.blocks
        .filter(b => !reorderedSet.has(b.id))
        .map(b => xMap[b.id] !== undefined ? { ...b, x: xMap[b.id] } : b);
      return { ...prev, blocks: [...reordered, ...remaining] };
    });
  }, [setConfig]);

  // ── Section Editing Actions ─────────────────────────────────────────────────────────

  const addSection = useCallback((blockId: string, row: number, slot: number, span: number, title?: string): string => {
    const sectionId = createId();
    setConfig(prev => {
      const block = prev.blocks.find(b => b.id === blockId);
      const sectionCount = block?.items.filter(item => item.type === 'section').length ?? 0;
      const sectionTitle = title ?? `Section ${sectionCount + 1}`;
      const newSection: SectionConfig = { id: sectionId, title: sectionTitle, widgets: [] };
      const newItem: BlockSectionItem = { type: 'section', row, slot, span, section: newSection };
      return {
        ...prev,
        blocks: prev.blocks.map(b => {
          if (b.id !== blockId) return b;
          // Remove any existing item overlapping the slot
          const filtered = b.items.filter(item => {
            if ((item.row ?? 1) !== row) return true;
            for (let s = item.slot; s < item.slot + item.span; s++) {
              if (s === slot) return false;
            }
            return true;
          });
          return { ...b, items: [...filtered, newItem] };
        }),
      };
    });
    return sectionId;
  }, [setConfig]);

  const removeSection = useCallback((blockId: string, sectionId: string) => {
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => {
        if (b.id !== blockId) return b;
        return { ...b, items: b.items.filter(item => !(item.type === 'section' && item.section.id === sectionId)) };
      }),
    }));
  }, [setConfig]);

  const updateSectionTitle = useCallback((blockId: string, sectionId: string, title: string) => {
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => {
        if (b.id !== blockId) return b;
        return {
          ...b,
          items: b.items.map(item =>
            item.type === 'section' && item.section.id === sectionId
              ? { ...item, section: { ...item.section, title } }
              : item,
          ),
        };
      }),
    }));
  }, [setConfig]);

  const toggleSectionHideTitle = useCallback((blockId: string, sectionId: string) => {
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => {
        if (b.id !== blockId) return b;
        return {
          ...b,
          items: b.items.map(item =>
            item.type === 'section' && item.section.id === sectionId
              ? { ...item, section: { ...item.section, hideTitle: !(item.section.hideTitle ?? false) } }
              : item,
          ),
        };
      }),
    }));
  }, [setConfig]);

  const toggleSectionCollapsible = useCallback((blockId: string, sectionId: string) => {
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => {
        if (b.id !== blockId) return b;
        return {
          ...b,
          items: b.items.map(item =>
            item.type === 'section' && item.section.id === sectionId
              ? { ...item, section: { ...item.section, collapsible: !(item.section.collapsible ?? false) } }
              : item,
          ),
        };
      }),
    }));
  }, [setConfig]);

  const assignWidgetToSection = useCallback((blockId: string, sectionId: string, row: number, slot: number, type: string, widgetConfig: Record<string, unknown>): string => {
    const widgetId = createId();
    const entry = registry.get(type);
    const newWidget = buildWidgetConfig(
      widgetId,
      type,
      { ...entry?.defaultConfig, ...widgetConfig },
    );
    const newBw = { row, slot, span: 1 as const, widget: newWidget };
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => {
        if (b.id !== blockId) return b;
        return {
          ...b,
          items: b.items.map(item => {
            if (item.type !== 'section' || item.section.id !== sectionId) return item;
            // Remove overlapping widget in the same row/slot
            const filtered = item.section.widgets.filter(bw => {
              if ((bw.row ?? 1) !== row) return true;
              for (let s = bw.slot; s < bw.slot + bw.span; s++) {
                if (s === slot) return false;
              }
              return true;
            });
            return { ...item, section: { ...item.section, widgets: [...filtered, newBw] } };
          }),
        };
      }),
    }));
    return widgetId;
  }, [registry, setConfig]);

  const removeWidgetFromSection = useCallback((blockId: string, sectionId: string, row: number, slot: number) => {
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => {
        if (b.id !== blockId) return b;
        return {
          ...b,
          items: b.items.map(item => {
            if (item.type !== 'section' || item.section.id !== sectionId) return item;
            return {
              ...item,
              section: {
                ...item.section,
                widgets: item.section.widgets.filter(bw => !(bw.slot === slot && (bw.row ?? 1) === row)),
              },
            };
          }),
        };
      }),
    }));
  }, [setConfig]);

  const moveWidgetInSection = useCallback((blockId: string, sectionId: string, fromRow: number, fromSlot: number, toRow: number, toSlot: number) => {
    if (fromRow === toRow && fromSlot === toSlot) return;
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => {
        if (b.id !== blockId) return b;
        return {
          ...b,
          items: b.items.map(item => {
            if (item.type !== 'section' || item.section.id !== sectionId) return item;
            const movingBw = item.section.widgets.find(bw => bw.slot === fromSlot && (bw.row ?? 1) === fromRow);
            if (!movingBw) return item;
            const sectionCols = item.span;
            const effectiveSpan = Math.min(movingBw.span, (sectionCols + 1) - toSlot);
            const maxRow = item.section.widgets.reduce((m, w) => Math.max(m, w.row ?? 1), 0);
            const nextRow = Math.max(maxRow, toRow) + 1;
            let ws = item.section.widgets.filter(bw => !(bw.slot === fromSlot && (bw.row ?? 1) === fromRow));
            ws = ws.map(bw => {
              if ((bw.row ?? 1) !== toRow) return bw;
              for (let s = toSlot; s < toSlot + effectiveSpan; s++) {
                if (s >= bw.slot && s < bw.slot + bw.span) return { ...bw, row: nextRow };
              }
              return bw;
            });
            return {
              ...item,
              section: { ...item.section, widgets: [...ws, { ...movingBw, row: toRow, slot: toSlot, span: effectiveSpan }] },
            };
          }),
        };
      }),
    }));
  }, [setConfig]);

  const updateSectionWidgetSpan = useCallback((blockId: string, sectionId: string, row: number, slot: number, span: number) => {
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => {
        if (b.id !== blockId) return b;
        return {
          ...b,
          items: b.items.map(item => {
            if (item.type !== 'section' || item.section.id !== sectionId) return item;
            const sectionCols = item.span;
            const effectiveSpan = Math.min(span, (sectionCols + 1) - slot);
            const maxRow = item.section.widgets.reduce((m, w) => Math.max(m, w.row ?? 1), 1);
            const nextRow = maxRow + 1;
            return {
              ...item,
              section: {
                ...item.section,
                widgets: item.section.widgets.map(bw => {
                  if (bw.slot === slot && (bw.row ?? 1) === row) return { ...bw, span: effectiveSpan };
                  if ((bw.row ?? 1) !== row) return bw;
                  for (let s = slot; s < slot + effectiveSpan; s++) {
                    if (s >= bw.slot && s < bw.slot + bw.span) return { ...bw, row: nextRow };
                  }
                  return bw;
                }),
              },
            };
          }),
        };
      }),
    }));
  }, [setConfig]);

  const updateWidgetConfigInSection = useCallback((sectionId: string, widgetId: string, partial: Partial<WidgetConfig['config']>) => {
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => ({
        ...b,
        items: b.items.map(item => {
          if (item.type !== 'section' || item.section.id !== sectionId) return item;
          return {
            ...item,
            section: {
              ...item.section,
              widgets: item.section.widgets.map(bw =>
                bw.widget.id === widgetId
                  ? { ...bw, widget: updateWidget(bw.widget, { config: { ...bw.widget.config, ...partial } }) }
                  : bw,
              ),
            },
          };
        }),
      })),
    }));
  }, [setConfig]);

  const getSectionById = useCallback((blockId: string, sectionId: string): SectionConfig | null => {
    const block = config.blocks.find(b => b.id === blockId);
    const item = block?.items.find(i => i.type === 'section' && i.section.id === sectionId) as BlockSectionItem | undefined;
    return item?.section ?? null;
  }, [config]);

  /** Change the column span of a section item in the parent block */
  const updateSectionItemSpan = useCallback((blockId: string, sectionId: string, newSpan: number) => {
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => {
        if (b.id !== blockId) return b;
        return {
          ...b,
          items: b.items.map(item =>
            item.type === 'section' && item.section.id === sectionId
              ? { ...item, span: newSpan }
              : item,
          ),
        };
      }),
    }));
  }, [setConfig]);

  /** Set the block's explicit row count (absolute) — called from RGL resize. */
  const setBlockRows = useCallback((blockId: string, rows: number) => {
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b =>
        b.id === blockId ? { ...b, minRows: Math.max(1, rows) } : b,
      ),
    }));
  }, [setConfig]);

  /** Set a section's explicit minimum inner row count — called from the drag-resize handle.
   * The minimum is clamped from below by the section's content-derived row count so the
   * section can never be dragged smaller than its widget content.
   */
  const updateSectionMinRows = useCallback((blockId: string, sectionId: string, rows: number) => {
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => {
        if (b.id !== blockId) return b;
        return {
          ...b,
          items: b.items.map(item => {
            if (item.type !== 'section' || item.section.id !== sectionId) return item;
            const maxCols = Math.max(1, item.span);
            // Content floor: derived rows ignoring current minInnerRows
            const contentFloor = getSectionEffectiveRows({ widgets: item.section.widgets, minInnerRows: undefined }, maxCols);
            return { ...item, section: { ...item.section, minInnerRows: Math.max(contentFloor, rows) } };
          }),
        };
      }),
    }));
  }, [setConfig]);

  /**
   * Delete an entirely empty row from a block.
   * Items in rows below the deleted row are shifted up by 1.
   * minRows is decremented so the block shrinks accordingly.
   */
  const deleteEmptyRow = useCallback((blockId: string, row: number) => {
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => {
        if (b.id !== blockId) return b;
        // Safety: bail if the row is not actually empty
        if (b.items.some(i => (i.row ?? 1) === row)) return b;
        const newItems = b.items.map(i => {
          const r = i.row ?? 1;
          return r > row ? { ...i, row: r - 1 } : i;
        });
        const newMinRows = b.minRows !== undefined ? Math.max(1, b.minRows - 1) : undefined;
        return { ...b, items: newItems, minRows: newMinRows };
      }),
    }));
  }, [setConfig]);

  /**
   * Change a block's column width to 1, 2, or 3.
   * Items (widgets/sections) that no longer fit within the new width are either
   * clamped (span trimmed) or removed entirely (slot is out of bounds).
   */
  const setBlockWidth = useCallback((blockId: string, w: number) => {
    setConfig(prev => ({
      ...prev,
      blocks: prev.blocks.map(b => {
        if (b.id !== blockId) return b;
        const newItems = b.items
          .filter(item => item.slot <= w) // drop items whose start col is beyond new width
          .map(item => {
            const overflow = (item.slot + item.span - 1) - w;
            if (overflow <= 0) return item;
            // Clamp span so it doesn't exceed the new right edge
            return { ...item, span: Math.max(1, item.span - overflow) };
          });
        return { ...b, w, items: newItems };
      }),
    }));
  }, [setConfig]);

  const setOuterCols = useCallback((cols: number) => {
    setConfig(prev => ({
      ...prev,
      outerCols: cols,
      // Clamp any blocks that are wider than the new column count
      blocks: prev.blocks.map(b => {
        const clampedW = Math.min(b.w ?? 3, cols);
        if (clampedW === (b.w ?? 3)) return b;
        const newItems = b.items
          .filter(item => item.slot <= clampedW)
          .map(item => {
            const overflow = (item.slot + item.span - 1) - clampedW;
            if (overflow <= 0) return item;
            return { ...item, span: Math.max(1, item.span - overflow) };
          });
        return { ...b, w: clampedW, items: newItems };
      }),
    }));
  }, [setConfig]);

  const createTheme = useCallback((name: string, colors: string[]): number => {
    const id = Date.now();
    setConfig(prev => ({
      ...prev,
      customThemes: [...(prev.customThemes ?? []), { id, name, colors }],
    }));
    return id;
  }, [setConfig]);

  const editingValue = useMemo(() => ({
    addBlock,
    addBlockWithWidget,
    removeBlock,
    updateBlockTitle,
    toggleBlockHideTitle,
    toggleBlockCollapsible,
    assignWidgetToSlot,
    removeWidgetAtSlot,
    moveWidget,
    moveWidgetFromSectionToBlock,
    moveWidgetFromBlockToSection,
    moveWidgetBetweenSections,
    updateWidgetSpan,
    updateWidgetRowSpan,
    autoSizeWidget,
    moveSection,
    updateWidgetConfig,
    updateWidgetDataSource,
    reorderBlocks,
    updateBlocksFromLayout,
    // section actions
    addSection,
    removeSection,
    updateSectionTitle,
    toggleSectionHideTitle,
    toggleSectionCollapsible,
    assignWidgetToSection,
    removeWidgetFromSection,
    moveWidgetInSection,
    updateSectionWidgetSpan,
    updateWidgetConfigInSection,
    getSectionById,
    updateSectionItemSpan,
    setBlockRows,
    setBlockWidth,
    updateSectionMinRows,
    deleteEmptyRow,
    createTheme,
    setOuterCols,
  }), [
    addBlock,
    addBlockWithWidget,
    removeBlock,
    updateBlockTitle,
    toggleBlockHideTitle,
    toggleBlockCollapsible,
    assignWidgetToSlot,
    removeWidgetAtSlot,
    moveWidget,
    moveWidgetFromSectionToBlock,
    moveWidgetFromBlockToSection,
    moveWidgetBetweenSections,
    updateWidgetSpan,
    updateWidgetRowSpan,
    autoSizeWidget,
    moveSection,
    updateWidgetConfig,
    updateWidgetDataSource,
    reorderBlocks,
    updateBlocksFromLayout,
    addSection,
    removeSection,
    updateSectionTitle,
    toggleSectionHideTitle,
    toggleSectionCollapsible,
    assignWidgetToSection,
    removeWidgetFromSection,
    moveWidgetInSection,
    updateSectionWidgetSpan,
    updateWidgetConfigInSection,
    getSectionById,
    updateSectionItemSpan,
    setBlockRows,
    setBlockWidth,
    updateSectionMinRows,
    deleteEmptyRow,
    createTheme,
    setOuterCols,
  ]);

  // ── Mutation Actions ───────────────────────────────────────────────────────
  const handleSave = useCallback(async (description?: string) => {
    await onSave?.(config, description);
    setIsDirty(false);
  }, [config, onSave]);

  const handleCancel = useCallback(() => {
    setConfigState(initialConfig);
    setIsDirty(false);
    onCancel?.();
  }, [initialConfig, onCancel]);

  const handleRevert = useCallback(async (versionId: string) => {
    await onRevert?.(versionId);
    setIsDirty(false);
  }, [onRevert]);

  const mutationValue = useMemo(() => ({
    onSave: handleSave,
    onCancel: handleCancel,
    onRevert: handleRevert,
  }), [handleSave, handleCancel, handleRevert]);

  // ── Provider tree ──────────────────────────────────────────────────────────
  return (
    <WidgetRegistryContext.Provider value={registryValue}>
      <DashboardConfigContext.Provider value={configValue}>
        <DashboardBuilderContext.Provider value={builderValue}>
          <DashboardBuilderEditingContext.Provider value={editingValue}>
            <DashboardMutationContext.Provider value={mutationValue}>
              {children}
            </DashboardMutationContext.Provider>
          </DashboardBuilderEditingContext.Provider>
        </DashboardBuilderContext.Provider>
      </DashboardConfigContext.Provider>
    </WidgetRegistryContext.Provider>
  );
}
